import { DrawerSheetTitle } from '../drawer-sheet';
import { SpanLoading } from '../fetch-status/span-loading';
import { TextTooltip } from '../text-tooltip';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { UserAvatar } from '../user-avatar';
import {
  InteractCloseIcon,
  InteractExitIcon,
  InteractMenuIcon,
  InteractSettingsIcon,
  InteractUserIcon,
} from '@/client/assets';
import { IS_DEBUG } from '@/client/confs/app';
import { useAuth } from '@/client/hooks/use-auth';
import { useSignedUserInfo } from '@/client/hooks/use-signed-user-info';
import { api } from '@/client/utils/api';
import { formatAddress } from '@/client/utils/format';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMedia from 'react-use/lib/useMedia';
import { useLocation } from 'wouter';

type WalletHeaderProps = {
  setOpen: (open: boolean) => void;
};

export const WalletHeader = ({ setOpen }: WalletHeaderProps) => {
  const isDesktop = useMedia('(min-width: 768px)');
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const { data: authData, logout, linkedWallet, linkWallet } = useAuth();
  const { data: signer } = useSignedUserInfo(api);
  const [isLoading, setIsLoading] = useState(false);

  // Use auth data uid as fallback when signer is still loading
  const userId = signer?.uid || authData?.uid;
  // Proven wallet from the session (/me). signer.eoaAddress is the raw DB
  // field, which for unlinked DID users is a did:… placeholder — never show it
  // as an address.
  const walletAddress = linkedWallet ?? (signer?.eoaAddress?.startsWith('0x') ? signer.eoaAddress : null);
  const handleLogout = async () => {
    setIsLoading(true);
    try {
      setOpen(false);
      await logout();
    } catch (error) {
      if (IS_DEBUG) console.error(error);
    }
    setIsLoading(false);
  };

  return (
    <DrawerSheetTitle className="flex justify-between items-center gap-2">
      <UserAvatar avatar={signer?.avatar} address={signer?.uid} fallback={signer?.name} className="size-12" />
      <div className="space-y-1 flex-1">
        <h3 className="font-semibold text-lg leading-[normal]">{signer?.name}</h3>
        {walletAddress ? (
          <TextTooltip tooltip={walletAddress} show={true} className="text-sm font-medium text-foreground/70">
            {formatAddress(walletAddress)}
          </TextTooltip>
        ) : (
          <button
            type="button"
            onClick={() => linkWallet?.()}
            className="text-sm font-medium text-primary hover:underline"
          >
            {t('ob-link-wallet', 'Connect & link wallet')}
          </button>
        )}
      </div>
      {isDesktop && (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              aria-label={t('more', 'More')}
              className="ring-none transition-all duration-200 hover:scale-90 focus:scale-80"
            >
              <InteractMenuIcon className="!size-6 fill-foreground/40" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="p-2 mt-1.5 translate-x-3 !rounded-4 bg-card">
            <DropdownMenuItem
              className="p-2"
              disabled={!userId}
              onClick={() => {
                if (!userId) return;
                setOpen(false);
                navigate(`/user/${userId}`);
              }}
            >
              <InteractUserIcon className="!size-5 fill-current" />
              <span className="font-medium text-sm leading-5">{t('profile')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="p-2"
              disabled={!userId}
              onClick={() => {
                if (!userId) return;
                setOpen(false);
                navigate(`/user/${userId}/settings`);
              }}
            >
              <InteractSettingsIcon className="!size-5 fill-current" />
              <span className="font-medium text-sm leading-5">{t('settings')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem className="p-2" disabled={isLoading} onClick={handleLogout}>
              <InteractExitIcon className="!size-5 fill-current" />
              {isLoading && <SpanLoading />}
              <span className="font-medium text-sm leading-5">{t('logout')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <Button
        size="icon"
        variant="ghost"
        aria-label={t('close', 'Close')}
        className="transition-all duration-200 hover:scale-90 focus:scale-80"
        onClick={() => {
          setOpen(false);
        }}
      >
        <InteractCloseIcon className="!size-6 fill-foreground/40" />
      </Button>
    </DrawerSheetTitle>
  );
};
